import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { canManageProgram } from '../../hooks/useProgramsApi';
import { getProgramMembers } from '../../api/programs';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import MembershipStatusBadge from '../../components/Program/MembershipStatusBadge';
import api from '../../api/api';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import { useRegistration } from '../../hooks/useRegistration';
import { auth } from '../../firebaseConfig';
import { programScorecardApi } from '../../api/scorecard';
import ScorecardTemplateBrowser from '../../components/Scorecard/ScorecardTemplateBrowser';

// New components
import SidebarNav from './components/SidebarNav';
import UnifiedPeopleTable from './components/UnifiedPeopleTable';
import usePeopleData from './hooks/usePeopleData';

// New Registration components (Phase 3)
import {
  RegistrationSetup,
  ApplicationPipeline,
  TemplateManager,
  TemplateBuilder,
  RegistrationInsights,
  CapacityManager
} from './components/registration';

import styles from './ProgramManage.module.css';

export default function ProgramManage() {
  const { clubId, programId } = useParams();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('all-people');
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [clubDetails, setClubDetails] = useState(null);
  const [programDetails, setProgramDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [userMembershipStatus, setUserMembershipStatus] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { userClubs, userProfile } = useUser();
  const { showSuccess, showError, showWarning } = useNotification();
  
  // Note: useRegistration is called later with more destructured properties
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  
  // Teams state (for team allocation column in people table)
  const [teams, setTeams] = useState([]);
  
  // Scorecard state
  const [programScorecard, setProgramScorecard] = useState(null);
  const [showScorecardBrowser, setShowScorecardBrowser] = useState(false);
  const [scorecardLoading, setScorecardLoading] = useState(false);

  // Registration state (Phase 3)
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [registrationRefreshTrigger, setRegistrationRefreshTrigger] = useState(0);
  
  // Get full registration data
  const {
    applications,
    loading: registrationLoading,
    refresh: refreshRegistration,
    approveApplication,
    rejectApplication,
    deleteApplication,
    config: registrationConfig,
    isRegistrationEnabled,
    updateConfig: updateRegistrationConfig
  } = useRegistration(programId);

  // Allowed/Force enable calculation
  const allowed = useMemo(() => {
    const explicit = canManageProgram(permissions);
    const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(clubId);
      const isOwnerOrManager = ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
      return isTargetClub && isOwnerOrManager;
    });
    
    const isDefinitelyCreator = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(clubId);
      return isTargetClub && role === 'creator';
    });
    
    return explicit || hasClubRole || isDefinitelyCreator;
  }, [permissions, userClubs, clubId]);

  const forceEnable = useMemo(() => {
    if (allowed) return true;
    
    const isCreatorOfTargetClub = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(clubId);
      return isTargetClub && role === 'creator';
    });
    
    return isCreatorOfTargetClub;
  }, [allowed, userClubs, clubId]);

  // Use consolidated people data hook
  const {
    data: peopleData,
    counts,
    filters,
    setFilters,
    filterOptions,
    isLoading: peopleLoading,
    selectedIds,
    selectedCount,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
    refreshAll: refreshPeople,
    mutations
  } = usePeopleData(programId, clubId, forceEnable);

  // Destructure mutation functions
  const { setRole, setStatus, remove, approve, decline, notify } = mutations;

  // Check user's membership status in this program
  useEffect(() => {
    const checkUserMembershipStatus = async () => {
      if (!programId || !userProfile?._id) {
        setMembershipLoading(false);
        return;
      }

      try {
        setMembershipLoading(true);
        const programMembers = await getProgramMembers(programId);
        const userMembership = Array.isArray(programMembers) 
          ? programMembers.find(m => (m.userId || m.user?._id) === userProfile._id)
          : null;
        
        setUserMembershipStatus(userMembership?.status || null);
      } catch (error) {
        console.error('Error checking user membership status:', error);
        setUserMembershipStatus(null);
      } finally {
        setMembershipLoading(false);
      }
    };

    checkUserMembershipStatus();
  }, [programId, userProfile?._id]);

  // Fetch club and program details
  const fetchDetails = useCallback(async () => {
    if (!clubId || !programId) return;
    setDetailsLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      
      const [programResponse, clubResponse, teamsResponse] = await Promise.all([
        fetch(`${backendUrl}/api/programs/${programId}`),
        fetch(`${backendUrl}/api/clubs/${clubId}`),
        fetch(`${backendUrl}/api/teams/program/${programId}`).catch(() => null),
      ]);
      
      if (programResponse.ok) {
        const programData = await programResponse.json();
        setProgramDetails(programData);
      }
      
      if (clubResponse.ok) {
        const clubData = await clubResponse.json();
        setClubDetails(clubData);
      }

      if (teamsResponse && teamsResponse.ok) {
        const teamsData = await teamsResponse.json();
        const teamList = teamsData?.teams || teamsData;
        setTeams(Array.isArray(teamList) ? teamList : []);
      }
    } catch (error) {
      console.error('Error fetching details:', error);
    } finally {
      setDetailsLoading(false);
    }
  }, [clubId, programId]);

  // Fetch permissions
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setPermissionsLoading(true);
        const data = await api.get(`/api/programs/${programId}/permissions`);
        if (mounted) setPermissions(data?.permissions || []);
      } catch (error) {
        if (mounted) setPermissions([]);
      } finally {
        if (mounted) setPermissionsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [programId, clubId]);

  // Fetch details on mount
  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  // Load scorecard configuration
  useEffect(() => {
    loadScorecardConfig();
  }, [programId]);

  const loadScorecardConfig = async () => {
    try {
      setScorecardLoading(true);
      const response = await programScorecardApi.getProgramScorecard(programId);
      setProgramScorecard(response.scorecard);
    } catch (err) {
      console.error('Error loading scorecard:', err);
    } finally {
      setScorecardLoading(false);
    }
  };

  // Redirect if not allowed
  useEffect(() => {
    if (!permissionsLoading && !allowed) {
      navigate(`/clubs/${clubId}`);
    }
  }, [allowed, clubId, navigate, permissionsLoading]);

  // Action handlers
  const onChangeRole = async (userId, role) => {
    try {
      await setRole(userId, role);
      await refreshPeople();
      showSuccess('Role updated successfully');
    } catch (e) {
      showError(e.payload?.error || 'Failed to set role');
    }
  };

  const onChangeStatus = async (userId, status) => {
    try {
      await setStatus(userId, status);
      await refreshPeople();
      showSuccess('Status updated successfully');
    } catch (e) {
      showError(e.payload?.error || e.message || 'Failed to change status');
    }
  };

  const onRemove = async (userId, userName = 'this member') => {
    setConfirmationModal({
      isOpen: true,
      title: `Delete ${userName}'s Membership`,
      message: `Are you sure you want to permanently DELETE ${userName}'s membership record?\n\nThis action:\n• Permanently removes their membership from the database\n• Cannot be undone\n• Does not affect their registration applications or payment records\n\nConsider using "Withdraw" or "Suspend" instead if you want to preserve the record.`,
      onConfirm: async () => {
        try {
          await remove(userId);
          await refreshPeople();
          showSuccess(`${userName}'s membership has been permanently deleted.`);
        } catch (e) {
          showError(e.payload?.error || e.message || 'Failed to delete membership');
        }
        setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      }
    });
  };

  const onWithdraw = async (userId, userName = 'this member') => {
    setConfirmationModal({
      isOpen: true,
      title: `Withdraw ${userName}'s Membership`,
      message: `Are you sure you want to WITHDRAW ${userName}'s membership?\n\nThis action:\n• Sets their status to "Withdrawn"\n• Removes their program access immediately\n• Requires them to reapply to rejoin the program\n• Preserves their membership record for audit purposes`,
      onConfirm: async () => {
        try {
          await setStatus(userId, 'Withdrawn');
          await refreshPeople();
          showWarning(`${userName}'s membership has been withdrawn.`);
        } catch (e) {
          showError(e.payload?.error || e.message || 'Failed to withdraw membership');
        }
        setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      }
    });
  };

  const onSuspend = async (userId, userName = 'this member') => {
    setConfirmationModal({
      isOpen: true,
      title: `Suspend ${userName}'s Membership`,
      message: `Are you sure you want to SUSPEND ${userName}'s membership?\n\nThis action:\n• Sets their status to "Suspended"\n• Removes their program access temporarily\n• Can be reversed by changing their status back to "Enrolled"\n• Preserves their membership record`,
      onConfirm: async () => {
        try {
          await setStatus(userId, 'Suspended');
          await refreshPeople();
          showWarning(`${userName}'s membership has been suspended.`);
        } catch (e) {
          showError(e.payload?.error || e.message || 'Failed to suspend membership');
        }
        setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      }
    });
  };

  const handleAllowApplicationSubmission = async (userId, userName = 'Unknown User') => {
    setConfirmationModal({
      isOpen: true,
      title: 'Allow Application Submission',
      message: `Allow ${userName} to submit a registration application? This will:\n\n• Remove them from the waitlist\n• Allow them to submit applications even if registration is closed\n• Let them go through the normal registration process like any new user`,
      onConfirm: async () => {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
          const response = await fetch(`${backendUrl}/api/programs/${programId}/members/${userId}/allow-application`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          showSuccess(`${userName} can now submit their registration application.`);
          
          try {
            await notify({
              type: 'waitlist_application_allowed',
              userId: userId,
              message: `Good news! You can now submit your registration application for the program.`
            });
          } catch (notifyError) {
            console.error('Failed to send notification:', notifyError);
          }
          
          await refreshPeople();
        } catch (error) {
          console.error('Allow application failed:', error);
          showError(error.message || 'Failed to allow application submission');
        } finally {
          setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        }
      }
    });
  };

  const handleDirectApproval = async (userId, userName = 'Unknown User') => {
    setConfirmationModal({
      isOpen: true,
      title: 'Approve for Membership',
      message: `Directly approve ${userName} for membership? They will be enrolled in the program immediately.`,
      onConfirm: async () => {
        try {
          await setStatus(userId, 'Enrolled');
          showSuccess(`${userName} has been approved and enrolled in the program.`);
          
          try {
            await notify({
              type: 'waitlist_approved',
              userId: userId,
              message: `Congratulations! You have been approved and enrolled in the program. Welcome!`
            });
          } catch (notifyError) {
            console.error('Failed to send notification:', notifyError);
          }
          
          await refreshPeople();
        } catch (error) {
          console.error('Direct approval failed:', error);
          showError(error.message || 'Failed to approve membership');
        } finally {
          setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        }
      }
    });
  };

  const approveApplicant = async (app) => {
    try {
      const applicationId = app._id;
      if (!applicationId) {
        showError('Cannot approve: Application ID not found');
        return;
      }
      await approve(applicationId, { role: 'Player', notes: 'Approved via UI' });
      await refreshPeople();
      showSuccess('Application approved successfully');
    } catch (e) {
      showError(e.payload?.error || e.message || 'Failed to approve application');
    }
  };

  const declineApplicant = async (app) => {
    try {
      const applicationId = app._id;
      if (!applicationId) {
        showError('Cannot decline: Application ID not found');
        return;
      }
      await decline(applicationId, { notes: 'Declined via UI' });
      await refreshPeople();
      showSuccess('Application declined');
    } catch (e) {
      showError(e.payload?.error || e.message || 'Failed to decline application');
    }
  };

  const sendBroadcast = async () => {
    const audience = window.prompt('Audience: Followers | Applicants | Enrolled');
    const subject = window.prompt('Subject');
    const message = window.prompt('Message');
    if (!audience || !subject || !message) return;
    try {
      await notify({ audience, subject, message });
      showSuccess('Notification queued');
    } catch (e) {
      showError(e.payload?.error || 'Failed to send');
    }
  };

  // Update filters based on section
  useEffect(() => {
    switch (activeSection) {
      case 'all-people':
        setFilters(prev => ({ ...prev, type: 'all' }));
        break;
      case 'members':
        setFilters(prev => ({ ...prev, type: 'members' }));
        break;
      case 'staff':
        setFilters(prev => ({ ...prev, type: 'staff' }));
        break;
      case 'applicants':
        setFilters(prev => ({ ...prev, type: 'applicants' }));
        break;
      case 'waitlist':
        setFilters(prev => ({ ...prev, type: 'waitlist' }));
        break;
      case 'followers':
        setFilters(prev => ({ ...prev, type: 'followers' }));
        break;
      default:
        break;
    }
  }, [activeSection, setFilters]);

  // Show loading while checking permissions or details
  if (permissionsLoading || detailsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading program management...</p>
        </div>
      </div>
    );
  }

  // Show access denied if not allowed
  if (!allowed) {
    return (
      <div className={styles.container}>
        <div className={styles.accessDenied}>
          <span className={styles.accessDeniedIcon}>🚫</span>
          <h2>Access Denied</h2>
          <p>You don't have permission to manage this program.</p>
          <button 
            className={styles.primaryButton} 
            onClick={() => navigate(`/clubs/${clubId}`)}
          >
            Return to Club
          </button>
        </div>
      </div>
    );
  }

  // Render content based on active section
  const renderContent = () => {
    // Dashboard section
    if (activeSection === 'dashboard') {
      return (
        <div className={styles.dashboardSection}>
          <h2 className={styles.sectionTitle}>📊 Program Dashboard</h2>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statIcon}>👥</span>
              <div className={styles.statInfo}>
                <span className={styles.statValue}>{counts.members}</span>
                <span className={styles.statLabel}>Members</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statIcon}>👔</span>
              <div className={styles.statInfo}>
                <span className={styles.statValue}>{counts.staff}</span>
                <span className={styles.statLabel}>Staff</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statIcon}>📝</span>
              <div className={styles.statInfo}>
                <span className={styles.statValue}>{counts.applicants}</span>
                <span className={styles.statLabel}>Applicants</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statIcon}>⏳</span>
              <div className={styles.statInfo}>
                <span className={styles.statValue}>{counts.waitlist}</span>
                <span className={styles.statLabel}>Waitlist</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statIcon}>👁️</span>
              <div className={styles.statInfo}>
                <span className={styles.statValue}>{counts.followers}</span>
                <span className={styles.statLabel}>Followers</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statIcon}>📊</span>
              <div className={styles.statInfo}>
                <span className={styles.statValue}>{counts.total}</span>
                <span className={styles.statLabel}>Total</span>
              </div>
            </div>
          </div>

          {counts.pendingApplicants > 0 && (
            <div className={styles.alertCard}>
              <span className={styles.alertIcon}>⚠️</span>
              <div className={styles.alertContent}>
                <strong>{counts.pendingApplicants} pending application(s)</strong>
                <p>Review and process these applications to manage your program enrollment.</p>
                <button 
                  className={styles.secondaryButton}
                  onClick={() => setActiveSection('applicants')}
                >
                  Review Applications
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }

    // People sections (all-people, members, staff, applicants, waitlist, followers)
    if (['all-people', 'members', 'staff', 'applicants', 'waitlist', 'followers'].includes(activeSection)) {
      return (
        <div className={styles.peopleSection}>
          <UnifiedPeopleTable
            data={peopleData}
            counts={counts}
            filters={filters}
            setFilters={setFilters}
            filterOptions={filterOptions}
            isLoading={peopleLoading}
            selectedIds={selectedIds}
            selectedCount={selectedCount}
            toggleSelection={toggleSelection}
            selectAll={selectAll}
            teams={teams}
            clearSelection={clearSelection}
            isSelected={isSelected}
            onRefresh={refreshPeople}
            onChangeRole={onChangeRole}
            onChangeStatus={onChangeStatus}
            onRemove={onRemove}
            onWithdraw={onWithdraw}
            onSuspend={onSuspend}
            onApprove={approveApplicant}
            onDecline={declineApplicant}
            onAllowApplication={handleAllowApplicationSubmission}
            onDirectApproval={handleDirectApproval}
            currentUserPermissions={permissions}
            isRegistrationEnabled={isRegistrationEnabled}
          />
        </div>
      );
    }

    // Registration section
    if (activeSection === 'registration') {
      return (
        <div className={styles.registrationSection}>
          <RegistrationSetup
            programId={programId}
            clubId={clubId}
            onOpenTemplateManager={() => setShowTemplateManager(true)}
            onOpenTemplateBuilder={() => {
              setEditingTemplate(null);
              setShowTemplateBuilder(true);
            }}
            refreshTrigger={registrationRefreshTrigger}
          />
        </div>
      );
    }

    // Applications section
    if (activeSection === 'applications') {
      return (
        <div className={styles.registrationSection}>
          <ApplicationPipeline
            programId={programId}
            applications={applications || []}
            loading={registrationLoading}
            onRefresh={refreshRegistration}
            onApprove={approveApplication}
            onReject={rejectApplication}
            onDelete={deleteApplication}
          />
        </div>
      );
    }

    // Scorecard section
    if (activeSection === 'scorecard') {
      return (
        <div className={styles.scorecardSection}>
          <h2 className={styles.sectionTitle}>🎯 Scorecard Configuration</h2>
          <p className={styles.sectionDescription}>
            Configure a scorecard template to enable live event scoring and statistics tracking for this program.
          </p>

          {scorecardLoading ? (
            <div className={styles.loading}>Loading scorecard configuration...</div>
          ) : programScorecard?.enabled ? (
            <div className={styles.scorecardConfigured}>
              <div className={styles.configCard}>
                <div className={styles.configHeader}>
                  <h3>Current Configuration</h3>
                  <span className={programScorecard.isLocked ? styles.lockedBadge : styles.unlockedBadge}>
                    {programScorecard.isLocked ? '🔒 Locked' : '🔓 Unlocked'}
                  </span>
                </div>

                <div className={styles.configDetails}>
                  <div className={styles.configRow}>
                    <span className={styles.configLabel}>Template:</span>
                    <strong className={styles.configValue}>{programScorecard.templateName}</strong>
                  </div>
                  <div className={styles.configRow}>
                    <span className={styles.configLabel}>Sport:</span>
                    <span className={styles.configValue}>{programDetails?.sport || 'N/A'}</span>
                  </div>
                  <div className={styles.configRow}>
                    <span className={styles.configLabel}>Type:</span>
                    <span className={styles.configValue}>
                      {programScorecard.isSystemDefault ? 'System Default' : 'Customized'}
                    </span>
                  </div>
                  {programScorecard.isLocked && (
                    <div className={styles.lockInfo}>
                      ⚠️ This scorecard is locked because it's being used by {programScorecard.eventsUsedCount} event(s). 
                      You cannot change or remove it while events are using it.
                    </div>
                  )}
                  <div className={styles.configRow}>
                    <span className={styles.configLabel}>Configured:</span>
                    <span className={styles.configValue}>
                      {programScorecard.configuredAt 
                        ? new Date(programScorecard.configuredAt).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                {!programScorecard.isLocked && (
                  <div className={styles.configActions}>
                    <button 
                      onClick={() => setShowScorecardBrowser(true)}
                      className={styles.primaryButton}
                    >
                      Change Template
                    </button>
                    <button 
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to remove the scorecard configuration?')) {
                          try {
                            await programScorecardApi.removeScorecard(programId);
                            showSuccess('Scorecard configuration removed');
                            loadScorecardConfig();
                          } catch (err) {
                            showError(err.message || 'Failed to remove scorecard');
                          }
                        }
                      }}
                      className={styles.dangerButton}
                    >
                      Remove Configuration
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.scorecardNotConfigured}>
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>🎯</div>
                <h3>No Scorecard Configured</h3>
                <p>Configure a scorecard template to enable live event scoring and statistics tracking.</p>
                <button 
                  onClick={() => setShowScorecardBrowser(true)}
                  className={styles.primaryButton}
                >
                  Configure Scorecard Template
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Capacity section
    if (activeSection === 'capacity') {
      return (
        <div className={styles.registrationSection}>
          <CapacityManager
            programId={programId}
            onUpdate={() => refreshRegistration()}
          />
        </div>
      );
    }

    // Analytics section
    if (activeSection === 'analytics') {
      return (
        <div className={styles.registrationSection}>
          <RegistrationInsights
            programId={programId}
            applications={applications || []}
            config={registrationConfig}
          />
        </div>
      );
    }

    // Notifications section
    if (activeSection === 'notifications') {
      return (
        <div className={styles.notificationsSection}>
          <h2 className={styles.sectionTitle}>📢 Notifications</h2>
          <p className={styles.sectionDescription}>
            Send notifications and announcements to your program members, applicants, or followers.
          </p>
          <button 
            className={styles.primaryButton}
            onClick={sendBroadcast}
          >
            📢 Send Broadcast Message
          </button>
        </div>
      );
    }

    // Default: Dashboard
    return (
      <div className={styles.dashboardSection}>
        <h2 className={styles.sectionTitle}>Select a section from the sidebar</h2>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button 
            className={styles.menuButton}
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            ☰
          </button>
          <div className={styles.breadcrumbs}>
            <button 
              className={styles.breadcrumbLink}
              onClick={() => navigate(`/clubs/${clubId}`)}
            >
              🏢 {clubDetails?.name || 'Club'}
            </button>
            <span className={styles.breadcrumbSeparator}>/</span>
            <button 
              className={styles.breadcrumbLink}
              onClick={() => navigate(`/clubs/${clubId}/programs/${programId}`)}
            >
              📋 {programDetails?.name || 'Program'}
            </button>
            {!membershipLoading && userMembershipStatus && (
              <MembershipStatusBadge status={userMembershipStatus} />
            )}
            <span className={styles.breadcrumbSeparator}>/</span>
            <span className={styles.breadcrumbCurrent}>Management</span>
          </div>
        </div>
        <div className={styles.headerRight}>
          <button 
            className={styles.headerButton}
            onClick={refreshPeople}
            disabled={peopleLoading}
          >
            🔄 {peopleLoading ? 'Loading...' : 'Refresh'}
          </button>
          <button 
            className={styles.headerButtonPrimary}
            onClick={sendBroadcast}
          >
            📢 Broadcast
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <div className={styles.layout}>
        {/* Sidebar */}
        <SidebarNav
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          counts={{
            ...counts,
            applications: applications?.length || 0
          }}
          isRegistrationEnabled={isRegistrationEnabled}
        />

        {/* Main Content */}
        <main className={styles.main}>
          {renderContent()}
        </main>
      </div>

      {/* Scorecard Browser Modal */}
      {showScorecardBrowser && (
        <ScorecardTemplateBrowser
          programId={programId}
          programSport={programDetails?.sport || 'Other'}
          onTemplateSelected={() => {
            showSuccess('Scorecard template configured successfully!');
            loadScorecardConfig();
            setShowScorecardBrowser(false);
          }}
          onCancel={() => setShowScorecardBrowser(false)}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        onClose={() => setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmationModal.onConfirm}
        message={confirmationModal.message}
      />

      {/* Template Manager Modal */}
      {showTemplateManager && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalLarge}>
            <TemplateManager
              clubId={clubId}
              onSelectTemplate={async (template) => {
                try {
                  // Update the registration config with the new template
                  await updateRegistrationConfig({
                    templateId: template._id || template.id
                  });
                  setShowTemplateManager(false);
                  await refreshRegistration();
                  // Trigger refresh in RegistrationSetup
                  setRegistrationRefreshTrigger(prev => prev + 1);
                  showSuccess(`Template "${template.name}" selected`);
                } catch (error) {
                  console.error('Error selecting template:', error);
                  showError(error.message || 'Failed to select template');
                }
              }}
              onCreateTemplate={(template) => {
                setShowTemplateManager(false);
                setEditingTemplate(template || null);
                setShowTemplateBuilder(true);
              }}
              onClose={() => setShowTemplateManager(false)}
            />
          </div>
        </div>
      )}

      {/* Template Builder Modal - Uses its own portal */}
      {showTemplateBuilder && (
        <TemplateBuilder
          template={editingTemplate}
          isEditing={!!editingTemplate}
          onTemplateCreated={(template) => {
            setShowTemplateBuilder(false);
            setEditingTemplate(null);
            refreshRegistration();
            showSuccess(editingTemplate ? 'Template updated' : 'Template created');
          }}
          onCancel={() => {
            setShowTemplateBuilder(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
}

